import { useState } from 'react';
import { useStepper } from "@/components/StepperProvider";
import { StepperNavigation } from "@/components/StepperNavigation";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {Separator} from "@/components/ui/separator";
import {api} from "@/services/auth-service";
import { useNavigate } from 'react-router-dom';

const ThemeStep = () => {
    const navigate = useNavigate();
    const { formData } = useStepper();
    const [themeData, setThemeData] = useState({
        storyType: formData?.storyType || '',
        primaryTheme: formData?.primaryTheme || '',
        customTheme: formData?.customTheme || ''
    });

    const storyTypes = [
        { id: 'adventure', label: 'Adventure Story', description: 'An exciting journey or quest' },
        { id: 'educational', label: 'Educational Tale', description: 'Learning about the world' },
        { id: 'bedtime', label: 'Bedtime Story', description: 'Gentle and soothing stories' },
        { id: 'funny', label: 'Humorous Story', description: 'Fun and silly adventures' },
        { id: 'fantasy', label: 'Magical Fantasy', description: 'Enchanted worlds and magic' }
    ];

    const themes = {
        moral: [
            { id: 'friendship', label: 'Friendship & Kindness' },
            { id: 'courage', label: 'Bravery & Courage' },
            { id: 'honesty', label: 'Honesty & Truth' },
            { id: 'sharing', label: 'Sharing & Caring' },
            { id: 'persistence', label: 'Never Give Up' },
            { id: 'differences', label: 'Embracing Differences' },
        ],
        learning: [
            { id: 'emotions', label: 'Understanding Feelings' },
            { id: 'nature', label: 'Nature & Environment' },
            { id: 'community', label: 'Community & Helping Others' },
            { id: 'creativity', label: 'Imagination & Creativity' },
            { id: 'change', label: 'Dealing with Change' },
        ]
    };

    const handleInputChange = (field: string, value: string) => {
        setThemeData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleNext = async () => {
        const finalFormData = { ...formData, ...themeData };
        // @ts-expect-error ignoring
        if (finalFormData.setting === "custom" && finalFormData.location) {
            // @ts-expect-error ignoring
            finalFormData.setting = finalFormData.location;
        }

        if (finalFormData.primaryTheme === "custom" && finalFormData.customTheme) {
            finalFormData.primaryTheme = finalFormData.customTheme;
        }
        // @ts-expect-error ignoring
        delete finalFormData.location;
        delete finalFormData.customTheme;

        // @ts-expect-error ignoring
        finalFormData.characters = finalFormData.characters.map(char => ({
            id: char.id,
            isMainCharacter: char.isMainCharacter,
            isAntagonist: char.isAntagonist
        }));

        const createStoryResponse = await api.post('/api/stories/', finalFormData);
        if (createStoryResponse.status === 201) {
            api.post('/api/stories/' + createStoryResponse.data.id + '/generate/');
            const generatedStoryIdResponse = await api.get('/api/stories/' + createStoryResponse.data.id + '/generated-story-id/');
            navigate('/story/' + generatedStoryIdResponse.data.generated_story_id);
        }
    };

    const isValid = themeData.storyType &&
        (themeData.primaryTheme || (themeData.primaryTheme === 'custom' && themeData.customTheme));

    return (
        <div className="space-y-6">
                <div className="space-y-6">
                    <div>
                        <Label className="mb-4 text-lg font-semibold">What type of story would you like to create?</Label>
                        <RadioGroup
                            value={themeData.storyType}
                            onValueChange={(value) => handleInputChange('storyType', value)}
                            className="grid gap-4 mt-2"
                        >
                            {storyTypes.map((type) => (
                                <label
                                    key={type.id}
                                    className={cn(
                                        "flex items-center space-x-2 rounded-lg border p-4 cursor-pointer bg-slate-50 dark:bg-transparent",
                                        themeData.storyType === type.id && "border-primary"
                                    )}
                                >
                                    <RadioGroupItem value={type.id} id={type.id} />
                                    <div className="flex flex-col">
                                        <span className="font-medium">{type.label}</span>
                                        <span className="text-sm text-muted-foreground">{type.description}</span>
                                    </div>
                                </label>
                            ))}
                        </RadioGroup>
                    </div>

                    <Separator />

                    <div className="space-y-4">
                        <Label className="text-lg font-semibold">Choose a primary theme or message</Label>

                        <div className="grid gap-4">
                            <div>
                                <h3 className="mb-2 font-medium text-muted-foreground">Moral Lessons</h3>
                                <div className="flex flex-wrap gap-2">
                                    {themes.moral.map((theme) => (
                                        <Badge
                                            key={theme.id}
                                            variant={themeData.primaryTheme === theme.id ? "default" : "secondary"}
                                            className="px-3 py-1 cursor-pointer"
                                            onClick={() => handleInputChange('primaryTheme', theme.id)}
                                        >
                                            {theme.label}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <h3 className="mb-2 font-medium text-muted-foreground">Learning & Growth</h3>
                                <div className="flex flex-wrap gap-2">
                                    {themes.learning.map((theme) => (
                                        <Badge
                                            key={theme.id}
                                            variant={themeData.primaryTheme === theme.id ? "default" : "secondary"}
                                            className="px-3 py-1 cursor-pointer"
                                            onClick={() => handleInputChange('primaryTheme', theme.id)}
                                        >
                                            {theme.label}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <h3 className="mb-2 font-medium text-muted-foreground">Custom Theme</h3>
                                <div className="flex flex-wrap gap-2">
                                    <Badge
                                        key={"custom"}
                                        variant={themeData.primaryTheme === "custom" ? "default" : "secondary"}
                                        className="px-3 py-1 cursor-pointer"
                                        onClick={() => handleInputChange('primaryTheme', "custom")}
                                    >
                                        Custom Theme
                                    </Badge>
                                </div>
                            </div>
                        </div>

                        {themeData.primaryTheme === 'custom' && (
                            <div className="mt-4">
                                <Label htmlFor="customTheme">Describe your theme or message</Label>
                                <Textarea
                                    id="customTheme"
                                    value={themeData.customTheme}
                                    onChange={(e) => handleInputChange('customTheme', e.target.value)}
                                    placeholder="What message would you like to convey through your story?"
                                    className="h-20 mt-2"
                                />
                            </div>
                        )}
                    </div>
                </div>

            <Separator />
            <StepperNavigation
                onNext={handleNext}
                nextDisabled={!isValid}
            />
        </div>
    );
};

export default ThemeStep;
